import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useAuth } from "./useAuth";

// Worker broadcasts run lifecycle events on a per-user channel `user:{user_id}`
// via agent.realtime_broadcast → realtime.send(payload, 'broadcast', channel).
// The DB function rejects channel/owner mismatches, so a subscriber only ever
// receives events for runs they own.
//
// Payload shape (apps/worker/src/realtime.ts):
//   { kind: EventKind, run_id, agent_id, conversation_id?, body }

export type AgentEventKind =
  | "run.queued"
  | "run.started"
  | "run.iteration_started"
  | "run.tool_call_started"
  | "run.tool_call_completed"
  | "run.message_appended"
  | "run.paused_for_approval"
  | "run.resumed_after_approval"
  | "run.succeeded"
  | "run.failed"
  | "run.cancelled";

export interface AgentEventPayload {
  kind: AgentEventKind;
  run_id: string;
  agent_id: string;
  conversation_id?: string | null;
  body?: Record<string, unknown>;
}

export function invalidateForEvent(qc: QueryClient, evt: AgentEventPayload): void {
  qc.invalidateQueries({ queryKey: ["agent-runs"] });
  qc.invalidateQueries({ queryKey: ["agent-run", evt.run_id] });

  if (evt.conversation_id) {
    qc.invalidateQueries({ queryKey: ["conversation", evt.conversation_id] });
    qc.invalidateQueries({ queryKey: ["conversation-messages", evt.conversation_id] });
  }

  if (evt.kind === "run.paused_for_approval" || evt.kind === "run.resumed_after_approval") {
    qc.invalidateQueries({ queryKey: ["approvals"] });
  }

  if (evt.kind === "run.tool_call_started" || evt.kind === "run.tool_call_completed") {
    qc.invalidateQueries({ queryKey: ["run-tool-calls", evt.run_id] });
  }
}

export function subscribeToAgentEvents(
  client: SupabaseClient,
  userId: string,
  onEvent: (evt: AgentEventPayload) => void,
): RealtimeChannel {
  const channel = client
    .channel(`user:${userId}`)
    .on<AgentEventPayload>("broadcast", { event: "*" }, (msg) => {
      const payload = msg.payload;
      if (payload && typeof payload.kind === "string" && typeof payload.run_id === "string") {
        onEvent(payload);
      }
    })
    .subscribe();
  return channel;
}

export function useAgentEvents(): void {
  const qc = useQueryClient();
  const { user } = useAuth();

  useEffect(() => {
    if (!user?.id) return;
    const channel = subscribeToAgentEvents(supabase, user.id, (evt) => {
      invalidateForEvent(qc, evt);
    });
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, qc]);
}
